import {
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isSameYear,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { WEEK_STARTS_ON } from '@/constants/app.constants';
import { formatDate, formatDisplayDate } from '@/utils/date';
import type { MapOn } from '../constants/moTravel.constants';
import { DATE_RANGE_SEPARATOR } from './dayLabels';

const MONTH_YEAR_FORMAT = 'MMMM yyyy';
const LONG_WEEKDAY_DATE_FORMAT = 'EEEE, dd MMM yyyy';
const DAY_OF_MONTH_FORMAT = 'dd';
const DAY_MONTH_FORMAT = 'dd MMM';

interface MoTravelPeriodRange {
  from: string;
  to: string;
}

export const isMultiDayPeriod = (mapOn: MapOn): boolean => mapOn !== 'DAILY';

export function getPeriodRange(date: string, mapOn: MapOn): MoTravelPeriodRange {
  const anchor = parseISO(date);
  if (mapOn === 'MONTHLY') {
    return { from: formatDate(startOfMonth(anchor)), to: formatDate(endOfMonth(anchor)) };
  }
  if (mapOn === 'WEEKLY') {
    const weekOptions = { weekStartsOn: WEEK_STARTS_ON } as const;
    return {
      from: formatDate(startOfWeek(anchor, weekOptions)),
      to: formatDate(endOfWeek(anchor, weekOptions)),
    };
  }
  return { from: date, to: date };
}

export function formatPeriodLabel(date: string, mapOn: MapOn): string {
  const { from, to } = getPeriodRange(date, mapOn);
  const start = parseISO(from);
  const end = parseISO(to);

  if (mapOn === 'MONTHLY') return format(start, MONTH_YEAR_FORMAT);
  if (mapOn === 'DAILY') return format(start, LONG_WEEKDAY_DATE_FORMAT);

  const endLabel = formatDisplayDate(end);
  if (isSameMonth(start, end)) {
    return `${format(start, DAY_OF_MONTH_FORMAT)}${DATE_RANGE_SEPARATOR}${endLabel}`;
  }
  if (isSameYear(start, end)) {
    return `${format(start, DAY_MONTH_FORMAT)}${DATE_RANGE_SEPARATOR}${endLabel}`;
  }
  return `${formatDisplayDate(start)}${DATE_RANGE_SEPARATOR}${endLabel}`;
}

export const isDayInPeriod = (range: MoTravelPeriodRange, day: string): boolean =>
  range.from <= day && day <= range.to;
